/**
 * AI Generation Service
 *
 * - Image generation: Calls Supabase Edge Function (Gemini API server-side)
 */

import { supabase } from '@/lib/supabase';
import {
  GenerationType,
  RoomType,
  AspectRatio,
} from '@/types';
import { computeAHashFromUrl } from '@/services/demoPairsService';

// ============================================================================
// Types
// ============================================================================

interface GenerateStageRequest {
  imagePath: string;
  roomType: string;
  projectId?: string;
  generationType: 'Empty' | 'Staged';
  keepKitchen: boolean;
  feedback?: string;
  customLabel?: string;
  imageId?: string; // Database ID of the original image entry
  sourceImagePath?: string; // For refinement: use this as input instead of original
  userId?: string; // User ID for credit deduction
  aspectRatio?: AspectRatio; // Aspect ratio for output image
  stagingStyle?: string; // Interior design style: 'modern' | 'luxury' | 'industrial'
  adminTest?: boolean; // Skip DB writes and credit deduction
  inputPhash?: string; // Perceptual hash of input image for demo pair matching
  variantCount?: number; // Number of parallel variants to generate (default 1, max 2)
  emptyBasePath?: string; // Catalog pipeline: pre-generated empty room to use as staging base
  // Admin catalog inspector (admin-only server-side):
  catalogPreviewOnly?: boolean;
  catalogConsultOverride?: string;
  catalogRenderTemplateOverride?: string;
  catalogPlan?: import('@/types').CatalogPlan;
  // Admin Leerräum-Berater (admin-only server-side): ersetzt empty_consult_system.
  emptyConsultOverride?: string;
}

interface GenerateStageResponse {
  success: boolean;
  imageUrl?: string;
  storagePath?: string;
  imageId?: string;
  error?: string;
  promptUsed?: string;
  variants?: Array<{ imageUrl: string; storagePath: string; modelUsed: string; promptVariant?: string }>;
  abTestId?: string;
  catalogDebug?: import('@/types').CatalogDebug;
}

// ============================================================================
// Main Generation Function
// ============================================================================

/**
 * Generate a staged or empty room image via Edge Function
 * 
 * @param storagePath - Path to the original image in Supabase Storage
 * @param projectId - The project ID for database association
 * @param roomType - The type of room (Living Room, Kitchen, etc.)
 * @param genType - Either 'Empty' or 'Staged'
 * @param keepKitchen - Whether to preserve kitchen elements
 * @param feedback - Optional feedback for regeneration
 * @param customLabel - Optional custom room label
 * @param imageId - Database ID of the original image (for updating existing record)
 * @param sourceImagePath - For refinement: use this generated image as input instead of original
 * @param userId - User ID for credit deduction
 * @param aspectRatio - Aspect ratio for output image ('1:1' or '4:3')
 * @returns Object with imageUrl and storagePath
 */
export const generateRoomImage = async (
  storagePath: string,
  projectId: string | undefined,
  roomType: RoomType | string,
  genType: GenerationType,
  keepKitchen: boolean = false,
  feedback?: string,
  customLabel?: string,
  imageId?: string, // Database ID to update the correct record
  sourceImagePath?: string, // For refinement mode: use this as AI input
  userId?: string, // User ID for credit deduction
  aspectRatio?: AspectRatio, // Aspect ratio for output image
  stagingStyle?: string, // Interior design style for staging
  adminTest?: boolean, // Skip DB writes and credit deduction
  variantCount?: number, // Number of parallel variants (default 1)
  emptyBasePath?: string, // Catalog pipeline: pre-generated empty room to use as staging base
  catalogPlan?: import('@/types').CatalogPlan, // Admin: deterministic replay of an analyzed plan
  emptyConsultOverride?: string, // Admin: ersetzt empty_consult_system (Leerräum-Berater)
): Promise<GenerateStageResponse> => {
  console.log(`[Generation] Calling Edge Function for ${genType} image...`);
  console.log(`[Generation] Original storage path: ${storagePath}`);
  console.log(`[Generation] Source image path (refinement): ${sourceImagePath || 'none - using original'}`);
  console.log(`[Generation] Project ID: ${projectId}`);
  console.log(`[Generation] Image DB ID: ${imageId || 'not provided'}`);
  console.log(`[Generation] User ID: ${userId || 'not provided'}`);
  console.log(`[Generation] Aspect Ratio: ${aspectRatio || '4:3 (default)'}`);
  console.log(`[Generation] Staging Style: ${stagingStyle || 'modern (default)'}`);

  // Compute perceptual hash of the original input image for demo pair matching.
  // Only for non-refinement, non-adminTest requests (refinement operates on a
  // previously generated image, not the original upload).
  let inputPhash: string | undefined;
  if (!sourceImagePath && !adminTest) {
    try {
      const { data: urlData } = supabase.storage.from('images').getPublicUrl(storagePath);
      inputPhash = await computeAHashFromUrl(urlData.publicUrl);
      console.log(`[Generation] Input pHash: ${inputPhash}`);
    } catch (hashErr) {
      console.warn('[Generation] Could not compute input pHash, demo pair check will be skipped:', hashErr);
    }
  }

  const request: GenerateStageRequest = {
    imagePath: storagePath,
    roomType: String(roomType),
    projectId,
    generationType: genType === GenerationType.EMPTY ? 'Empty' : 'Staged',
    keepKitchen,
    feedback,
    customLabel,
    imageId,
    sourceImagePath,
    userId,
    aspectRatio,
    stagingStyle,
    adminTest,
    inputPhash,
    variantCount,
    emptyBasePath,
    catalogPlan,
    emptyConsultOverride: emptyConsultOverride?.trim() ? emptyConsultOverride : undefined,
  };

  // Get session for auth
  const { data: { session } } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke<GenerateStageResponse>(
    'generate-stage',
    { 
      body: request,
      headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
    }
  );

  if (error) {
    console.error('[Generation] Edge Function error:', error);

    // Extract actual error from FunctionsHttpError response body
    let errorMessage = error.message || 'Unbekannter Fehler';
    try {
      if (error.context && typeof error.context.json === 'function') {
        const errorBody = await error.context.json();
        errorMessage = errorBody?.error || errorMessage;
      }
    } catch {
      // Context already consumed or not available
    }

    throw new Error(`Generierung fehlgeschlagen: ${errorMessage}`);
  }

  if (!data || !data.success) {
    const errorMsg = data?.error || 'Unbekannter Fehler';
    console.error('[Generation] Generation failed:', errorMsg);
    throw new Error(errorMsg);
  }

  // Fix local dev: edge function returns internal Docker URL (kong:8000) — rewrite to configured Supabase URL
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  if (data.imageUrl) {
    data.imageUrl = data.imageUrl.replace(/https?:\/\/kong:\d+/, supabaseUrl);
  }
  if (data.variants) {
    data.variants = data.variants.map((v: { imageUrl: string; storagePath: string; modelUsed: string; promptVariant?: string }) => ({
      ...v,
      imageUrl: v.imageUrl.replace(/https?:\/\/kong:\d+/, supabaseUrl),
    }));
  }

  console.log(`[Generation] Successfully generated ${genType} image (${data.variants?.length ?? 1} variant(s))`);

  return data;
};

// ============================================================================
// Admin: Katalog-Staging Analyse (Trockenlauf, kein Bild)
// ============================================================================

export interface AnalyzeCatalogParams {
  emptyStoragePath: string; // freigegebenes Leerzimmer (dient als imagePath + emptyBasePath)
  projectId?: string;
  roomType: string;
  keepKitchen: boolean;
  userId?: string;
  aspectRatio?: AspectRatio;
  stagingStyle?: string;
  consultOverride?: string;
  renderTemplateOverride?: string;
}

export const analyzeCatalogStaging = async (
  params: AnalyzeCatalogParams,
): Promise<import('@/types').CatalogDebug | null> => {
  const request: GenerateStageRequest = {
    imagePath: params.emptyStoragePath,
    emptyBasePath: params.emptyStoragePath,
    roomType: params.roomType,
    projectId: params.projectId,
    generationType: 'Staged',
    keepKitchen: params.keepKitchen,
    userId: params.userId,
    aspectRatio: params.aspectRatio,
    stagingStyle: params.stagingStyle,
    adminTest: true, // keine DB-Writes / kein Credit
    variantCount: 1,
    catalogPreviewOnly: true,
    catalogConsultOverride: params.consultOverride?.trim() ? params.consultOverride : undefined,
    catalogRenderTemplateOverride: params.renderTemplateOverride?.trim() ? params.renderTemplateOverride : undefined,
  };

  const { data: { session } } = await supabase.auth.getSession();
  const { data, error } = await supabase.functions.invoke<GenerateStageResponse>(
    'generate-stage',
    {
      body: request,
      headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
    },
  );
  if (error) {
    console.error('[Catalog-Analyze] Edge Function error:', error);
    throw new Error(await extractEdgeError(error, 'Analyse fehlgeschlagen'));
  }
  if (!data?.success) {
    throw new Error(data?.error || 'Analyse fehlgeschlagen');
  }
  return data.catalogDebug ?? null;
};

// ============================================================================
// Verification-Only Mode (Admin Prompt Testing)
// ============================================================================

/**
 * Run verification against original + generated image with a custom prompt.
 * Returns the verification result without generating a new image.
 */
export const runVerificationOnly = async (
  originalPath: string,
  generatedPath: string,
  verificationPrompt?: string,
): Promise<{ needsCorrection: boolean; correctionPrompt?: string }> => {
  const { data: { session } } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke('generate-stage', {
    body: {
      verifyOnly: true,
      originalPath,
      generatedPath,
      verificationPrompt,
    },
    headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
  });

  if (error) {
    throw new Error(`Verification failed: ${error.message}`);
  }

  if (!data?.success || !data?.verification) {
    throw new Error(data?.error || 'Verification returned no result');
  }

  return {
    needsCorrection: data.verification.needsCorrection ?? false,
    correctionPrompt: data.verification.correctionPrompt,
  };
};

// ============================================================================
// Legacy Support (for non-project images)
// ============================================================================

// ============================================================================
// Stage Refinement (admin-gated box-mask edits)
// ============================================================================

import type { BoxCoordinates } from '@/types';

export interface StageRefinementResult {
  ok: boolean;
  storage_path?: string;
  scope?: 'global' | 'local';
  intent?: 'cosmetic' | 'structural';
  confidence?: number;
  /**
   * Advisory (no image generated, no credit charged): the classifier judged this
   * better done by a full regeneration than an edit (e.g. a camera-perspective
   * change). The UI offers "Neu generieren" + a "Trotzdem verfeinern" that
   * re-submits with forceEdit. Mirrors floorplan's rebuild_suggested.
   */
  regenerate_suggested?: boolean;
  regenerate_reason?: string | null;
}

/**
 * Supabase FunctionsHttpError only exposes a generic "non-2xx status code"
 * message; the real error code (e.g. 'classifier_error', 'not_authorized',
 * 'banana_error') lives in the JSON response body. Extract it so failures are
 * diagnosable instead of opaque.
 */
async function extractEdgeError(error: unknown, fallback: string): Promise<string> {
  const e = error as { message?: string; context?: { json?: () => Promise<{ error?: string }> } };
  try {
    if (e?.context && typeof e.context.json === 'function') {
      const body = await e.context.json();
      if (body?.error) return body.error;
    }
  } catch {
    // response body already consumed / not JSON — fall through to message
  }
  return e?.message ?? fallback;
}

/**
 * Refine a previously staged image — supports both text-only feedback (no box) and
 * box-mask edits. Routes to generate-stage-refinement edge function.
 * Admin-gated server-side via app_settings flag.
 *
 * @param storagePath - Exact storage path of the (possibly A/B-variant) image to edit
 * @param imageDbId - images.id (GenerationResult.dbImageId) — locates the original photo + links the history row
 * @param versionType - 'Staged' | 'Empty' — inherited onto the new history row
 * @param userPrompt - User's refinement instruction
 * @param boxCoords - Optional bounding-box selection (normalized 0..1)
 */
export async function requestStageRefinement(params: {
  storagePath: string;
  imageDbId: string;
  versionType: 'Staged' | 'Empty';
  userPrompt: string;
  boxCoords?: BoxCoordinates;
  // Admin prompt-inspector: run with an edited final prompt + fixed routing
  // (skips classifier + template assembly server-side).
  promptOverride?: string;
  scopeOverride?: 'global' | 'local';
  intentOverride?: 'cosmetic' | 'structural';
  // "Trotzdem verfeinern": bypass the regenerate-suggestion + run the edit anyway.
  forceEdit?: boolean;
}): Promise<StageRefinementResult> {
  // No image_history pre-lookup: A/B / parallel variants each have their own
  // storage_path, but generate-stage records only ONE history row — so looking
  // up by storage_path failed for any non-first variant ("Verlaufseintrag nicht
  // gefunden"). We pass the exact storagePath + images.id directly instead, so
  // any variant is editable regardless of what's in image_history.
  const { data: { session } } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke('generate-stage-refinement', {
    body: {
      storagePath: params.storagePath,
      imageDbId: params.imageDbId,
      versionType: params.versionType,
      userPrompt: params.userPrompt,
      boxCoordinates: params.boxCoords,
      promptOverride: params.promptOverride,
      scopeOverride: params.scopeOverride,
      intentOverride: params.intentOverride,
      forceEdit: params.forceEdit,
    },
    headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
  });

  if (error) {
    console.error('[geminiService.requestStageRefinement] error', error);
    throw new Error(friendlyStageRefinementError(await extractEdgeError(error, 'Refinement fehlgeschlagen')));
  }
  if (!data || (data as { error?: string }).error) {
    throw new Error(friendlyStageRefinementError((data as { error?: string })?.error ?? 'Refinement fehlgeschlagen'));
  }
  return data as StageRefinementResult;
}

/**
 * Map known refinement error codes to a user-facing German message. Unknown
 * codes pass through unchanged (admins also get the raw detail server-side).
 */
const STAGE_REFINEMENT_ERROR_MESSAGES: Record<string, string> = {
  not_authenticated:
    'Deine Sitzung ist abgelaufen. Bitte melde dich neu an und versuche es dann erneut.',
  feature_disabled:
    'Die Nachbessern-Funktion ist aktuell deaktiviert. Bitte versuche es später erneut.',
  // Legacy-Code älterer Function-Deployments — konnte Auth-Fehler, Flag aus
  // oder Admin-Inspector bedeuten. Neutral formulieren.
  admin_required:
    'Die Nachbessern-Funktion ist aktuell nicht verfügbar. Bitte versuche es später erneut.',
  insufficient_credits:
    'Dein Guthaben reicht nicht für diese Nachbesserung. Bitte lade unter „Abo & Guthaben" Credits nach.',
  invalid_body:
    'Die Anfrage war unvollständig. Bitte lade die Seite neu und versuche es erneut.',
  reference_fetch_error:
    'Das Ausgangsbild konnte nicht geladen werden. Bitte lade die Seite neu und versuche es erneut.',
  not_authorized:
    'Du hast keinen Zugriff auf dieses Bild.',
  classifier_error:
    'Deine Anweisung konnte gerade nicht analysiert werden. Bitte versuche es in einem Moment erneut — es wurden keine Credits verbraucht.',
  storage_error:
    'Das Ergebnis konnte nicht gespeichert werden. Bitte versuche es erneut.',
  openai_key_missing:
    'Der Bild-Editor ist serverseitig nicht vollständig konfiguriert. Bitte kontaktiere den Support.',
  banana_error:
    'Die Nachbesserung ist leider fehlgeschlagen. Bitte versuche es erneut — es wurden keine Credits verbraucht.',
};

function friendlyStageRefinementError(raw: string): string {
  if (raw.startsWith('model_overloaded')) {
    return 'Das Bild-Modell ist gerade überlastet. Bitte in einem Moment nochmal versuchen — es wurden keine Credits verbraucht.';
  }
  if (raw.startsWith('banana_error:')) {
    // Admin-Variante mit Roh-Detail (banana_error: <detail>) — fürs Debugging durchreichen.
    return raw;
  }
  return STAGE_REFINEMENT_ERROR_MESSAGES[raw] ?? raw;
}

// ── Admin prompt-inspector ──────────────────────────────────────────────────

export interface StageRefinementPreview {
  ok: boolean;
  preview: true;
  scope: 'global' | 'local';
  intent: 'cosmetic' | 'structural';
  confidence: number;
  target: string | null;
  /** Object-anchored English location phrase from the multimodal classifier (local + box only); null otherwise. */
  region_description?: string | null;
  /**
   * Stage 2 output — the classification-aware, precise English edit
   * instruction produced by classify-stage-intent's rewrite step. Null when
   * the classifier itself was skipped (admin scope/intent-override path).
   */
  rewrittenPrompt: string | null;
  provider: 'gemini' | 'openai';
  model: string;
  templateKey: string;
  finalPrompt: string;
  /**
   * The effective Stage-1 (classifier) + Stage-2 (rewrite) system prompts —
   * the admin's own override if one was sent, else the DB row, else the
   * hardcoded default. Used to seed the inline stage-prompt editors in the
   * refinement modal so an admin can live-tune them.
   */
  classifierPrompt: string;
  rewritePrompt: string;
  /**
   * Which per-case rewrite prompt (`stage_reprompt_rewrite_${scope}_${intent}`)
   * `rewritePrompt` belongs to. The modal keys its Save + case-tagged override
   * to this so a rewrite prompt edited for one case never leaks onto another.
   */
  rewriteTemplateKey: string;
  /**
   * The Stage-3 assembly template (the recipe with {{USER_PROMPT}}/{{REGION}}
   * placeholders) for this case — `templateKey` is its prompt_templates key.
   * Lets the modal seed + save the assembly template inline.
   */
  assemblyTemplate: string;
  /** Classifier's final_action + reason — the admin inspector shows when a regeneration is suggested. */
  final_action?: 'edit' | 'regenerate';
  regenerate_reason?: string | null;
}

/**
 * Admin-only dry-run: classify + assemble the final prompt and return the
 * routing decision WITHOUT generating an image (no credit, no OpenAI key). Lets
 * an admin inspect which class the prompt is routed to and edit the final prompt
 * before running the real refinement.
 */
export async function requestStageRefinementPreview(params: {
  storagePath: string;
  imageDbId: string;
  versionType: 'Staged' | 'Empty';
  userPrompt: string;
  boxCoords?: BoxCoordinates;
  /**
   * Admin inline stage-prompt tuning: run the classifier / rewrite with these
   * system prompts instead of the DB rows. Only forwarded (server-side) for
   * admins. Omit on the first analyze to receive the current prompts back.
   */
  classifierPromptOverride?: string;
  rewritePromptOverride?: string;
  /** Which per-case rewrite prompt the override was edited for (scope_intent key). */
  rewritePromptOverrideKey?: string;
  /** Inline Stage-3 tuning: assembly template + the case key it was edited for. */
  assemblyTemplateOverride?: string;
  assemblyTemplateOverrideKey?: string;
}): Promise<StageRefinementPreview> {
  const { data: { session } } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke('generate-stage-refinement', {
    body: {
      previewOnly: true,
      storagePath: params.storagePath,
      imageDbId: params.imageDbId,
      versionType: params.versionType,
      userPrompt: params.userPrompt,
      boxCoordinates: params.boxCoords,
      classifierPromptOverride: params.classifierPromptOverride,
      rewritePromptOverride: params.rewritePromptOverride,
      rewritePromptOverrideKey: params.rewritePromptOverrideKey,
      assemblyTemplateOverride: params.assemblyTemplateOverride,
      assemblyTemplateOverrideKey: params.assemblyTemplateOverrideKey,
    },
    headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
  });

  if (error) {
    console.error('[geminiService.requestStageRefinementPreview] error', error);
    throw new Error(await extractEdgeError(error, 'Analyse fehlgeschlagen'));
  }
  if (!data || (data as { error?: string }).error) {
    throw new Error((data as { error?: string })?.error ?? 'Analyse fehlgeschlagen');
  }
  return data as StageRefinementPreview;
}
